// Generalized version history drawer.
// Takes a VersionAdapter so it can display history for any versioned entity
// (agent files, transforms, etc.) without hard-coding the domain.
//
// Phase 1 agent files continue to work via the agentFileVersionAdapter.
// Transforms use transformVersionAdapter.

import { useState } from "react";
import { History } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "@/lib/agent-skills/timeFormat";
import { GenericVersionDiff } from "@/components/history/GenericVersionDiff";
import { RestoreConfirmDialog } from "@/components/scratchpad/RestoreConfirmDialog";
import type {
  VersionAdapter,
  VersionRecord,
  CurrentState,
} from "@/components/history/VersionAdapter";

interface GenericHistoryDrawerProps<
  TVersion extends VersionRecord,
  TCurrentState extends CurrentState,
> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The ID of the entity whose history is shown. */
  entityId: string;
  /** The entity's current version number (for optimistic locking on restore). */
  currentVersion: number;
  /** Whether the editor currently has unsaved changes. */
  isDirty: boolean;
  /** The current (possibly unsaved) editor state — used to show diff against selected version. */
  currentState: TCurrentState;
  /** Adapter providing version hooks + formatting. */
  adapter: VersionAdapter<TVersion, TCurrentState>;
  /** Title shown in the drawer header. Default: "Version history". */
  title?: string;
}

export function GenericHistoryDrawer<
  TVersion extends VersionRecord,
  TCurrentState extends CurrentState,
>({
  open,
  onOpenChange,
  entityId,
  currentVersion,
  isDirty,
  currentState,
  adapter,
  title = "Version history",
}: GenericHistoryDrawerProps<TVersion, TCurrentState>) {
  const { data: versions, isLoading } = adapter.useVersions(entityId);
  const restoreMutation = adapter.useRestore();

  const [selectedVersion, setSelectedVersion] = useState<TVersion | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  function handleRestoreClick() {
    if (!selectedVersion) return;
    if (isDirty) {
      setConfirmDialogOpen(true);
    } else {
      doRestore();
    }
  }

  function doRestore() {
    if (!selectedVersion) return;
    void restoreMutation.mutateAsync({
      entityId,
      version: selectedVersion,
      expectedVersion: currentVersion,
    });
    onOpenChange(false);
    setSelectedVersion(null);
  }

  const diffContent = selectedVersion ? adapter.formatVersion(selectedVersion, currentState) : null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[480px] flex flex-col p-0">
          <SheetHeader className="h-12 border-b px-4 flex flex-row items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-muted-foreground" />
              <SheetTitle className="text-base font-display font-semibold">{title}</SheetTitle>
            </div>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="w-8 h-8">
                ×
              </Button>
            </SheetClose>
          </SheetHeader>

          <div className="flex flex-1 min-h-0 divide-x divide-border">
            {/* Version list */}
            <div className="w-[180px] shrink-0 flex flex-col overflow-y-auto p-2 space-y-1">
              {isLoading && (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 rounded-md" />
                  ))}
                </>
              )}

              {!isLoading && versions && versions.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-8">No versions yet.</p>
              )}

              {!isLoading &&
                versions?.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersion(v)}
                    className={[
                      "w-full text-left rounded-md p-3 hover:bg-white/[0.06] transition-colors",
                      selectedVersion?.id === v.id ? "bg-indigo-500/15" : "",
                    ].join(" ")}
                  >
                    <p className="text-sm font-medium">
                      v{v.version_number} · {formatDistanceToNow(v.created_at)}
                    </p>
                    {v.note && (
                      <p className="text-xs text-muted-foreground italic truncate">
                        &ldquo;{v.note}&rdquo;
                      </p>
                    )}
                  </button>
                ))}
            </div>

            {/* Diff view */}
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
              {diffContent ? (
                <div className="flex-1 overflow-auto p-3">
                  <GenericVersionDiff
                    beforeText={diffContent.before}
                    afterText={diffContent.after}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
                  Select a version to see the diff.
                </div>
              )}

              {selectedVersion && (
                <div className="shrink-0 border-t p-3">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={handleRestoreClick}
                    disabled={restoreMutation.isPending}
                  >
                    Restore v{selectedVersion.version_number}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {selectedVersion && (
        <RestoreConfirmDialog
          open={confirmDialogOpen}
          onOpenChange={setConfirmDialogOpen}
          versionNumber={selectedVersion.version_number}
          onConfirm={doRestore}
        />
      )}
    </>
  );
}
